export type EventRole = {
  id: string;
  slug: string;
  label: string;
  description: string | null;
  price_cents: number | null;
  currency: string;
  payment_policy: string;
  sort_order: number;
};

export type EventHost = {
  id: string;
  slug: string;
  name: string;
};

export type PublicEvent = {
  id: string;
  slug: string;
  title: string;
  eyebrow: string | null;
  summary: string | null;
  description: string | null;
  starts_at: string;
  ends_at: string;
  timezone: string;
  venue_name: string | null;
  address_line: string | null;
  postal_code: string | null;
  city: string | null;
  hosts: EventHost[];
  event_roles: EventRole[];
};

export type PublicEventsLoadState = "loading" | "ready" | "error";

export type PublicEventsListView = "loading" | "events" | "empty" | "missing" | "error";

export type PublicEventHostCatalog = {
  events?: Array<{
    id: string;
    hosts?: EventHost[];
  }>;
};

export const PUBLIC_EVENT_SELECT =
  "id, slug, title, eyebrow, summary, description, starts_at, ends_at, timezone, venue_name, address_line, postal_code, city, event_roles(id, slug, label, description, price_cents, currency, payment_policy, sort_order)";

export function formatEventDate(event: Pick<PublicEvent, "starts_at" | "timezone">) {
  return new Intl.DateTimeFormat("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: event.timezone,
  }).format(new Date(event.starts_at));
}

export function formatEventTime(event: Pick<PublicEvent, "starts_at" | "ends_at" | "timezone">) {
  const formatter = new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: event.timezone,
  });

  return `${formatter.format(new Date(event.starts_at))}–${formatter.format(
    new Date(event.ends_at),
  )} Uhr`;
}

export function formatEventSchedule(
  event: Pick<PublicEvent, "starts_at" | "ends_at" | "timezone">,
) {
  const start = eventDateParts(event.starts_at, event.timezone);
  const end = eventDateParts(event.ends_at, event.timezone);

  if (start.key === end.key) {
    return `${formatEventDate(event)}, ${formatEventTime(event)}`;
  }

  if (start.year === end.year && start.month === end.month) {
    return `${start.day}.–${end.day}. ${end.month} ${end.year}`;
  }

  if (start.year === end.year) {
    return `${start.day}. ${start.month}–${end.day}. ${end.month} ${end.year}`;
  }

  return `${start.day}. ${start.month} ${start.year}–${end.day}. ${end.month} ${end.year}`;
}

function eventDateParts(value: string, timeZone: string) {
  const formatter = new Intl.DateTimeFormat("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone,
  });
  const parts = formatter.formatToParts(new Date(value));
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const year = parts.find((part) => part.type === "year")?.value ?? "";
  return { day, month, year, key: `${year}-${month}-${day}` };
}

export function formatPrice(priceCents: number | null, currency: string, paymentPolicy?: string) {
  if (paymentPolicy === "free" || priceCents === 0) return "Beitrag offen";
  if (priceCents === null) return "Preis folgt";

  return `${new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
  }).format(priceCents / 100)} inkl. MwSt.`;
}

export function formatLocation(
  event: Pick<PublicEvent, "venue_name" | "address_line" | "postal_code" | "city">,
) {
  return [
    event.venue_name,
    event.address_line?.startsWith("https://") ? null : event.address_line,
    [event.postal_code, event.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
}

export function formatEventMode(event: Pick<PublicEvent, "venue_name">) {
  const venue = (event.venue_name ?? "").toLowerCase();
  if (venue.includes("online") || venue.includes("meet") || venue.includes("zoom")) {
    return "Online";
  }
  return "Präsenzveranstaltung";
}

export function formatEventHosts(event: Pick<PublicEvent, "hosts">) {
  const names = event.hosts.map((host) => host.name).filter(Boolean);
  if (names.length === 0) return "Leitung folgt";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} & ${names.at(-1)}`;
}

export function eventHostsById(catalog: PublicEventHostCatalog | null | undefined) {
  return new Map((catalog?.events ?? []).map((event) => [event.id, event.hosts ?? []]));
}

export function sortEventRoles(roles: EventRole[]) {
  return [...roles].sort((first, second) => first.sort_order - second.sort_order);
}

export function normalizePublicEvents(events: PublicEvent[]) {
  return events.map((event) => ({
    ...event,
    hosts: event.hosts ?? [],
    event_roles: sortEventRoles(event.event_roles ?? []),
  }));
}

export function publicEventsListView({
  status,
  events,
  eventSlug,
}: {
  status: PublicEventsLoadState;
  events: PublicEvent[];
  eventSlug?: string;
}): PublicEventsListView {
  if (status === "loading") return "loading";
  if (status === "error") return "error";
  if (events.length > 0) return "events";
  return eventSlug ? "missing" : "empty";
}
